import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import Checkbox from '@/components/UI/FormElements/Checkbox/Checkbox';
import { infoToast, successToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { ResponseType } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';
import { useState, type FC } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form@latest';

type ExportTypeName = 'master' | 'summary' | 'prism' | 'dosing';

interface ExportFormValues {
  master: boolean;
  summary: boolean;
  prism: boolean;
  dosing: boolean;
}

interface ExportTypeOption {
  name: ExportTypeName;
  label: string;
  subLabel: string;
  disabled?: boolean;
}

interface ExportV2Props {
  study: Study;
}

const DEFAULT_FORM_VALUES: ExportFormValues = {
  master: false,
  summary: false,
  prism: false,
  dosing: false,
};

const buildExportTypes = (study: Study): Array<ExportTypeOption> => [
  {
    name: 'master',
    label: 'Raw study data',
    subLabel: 'All animals and their measurements',
  },
  {
    name: 'summary',
    label: 'Study group report',
    subLabel: 'Includes relative values',
    disabled: _isEmpty(study.study_groups),
  },
  {
    name: 'prism',
    label: 'Prism report',
    subLabel: 'Including relative values in PZFX format',
    disabled: _isEmpty(study.study_groups),
  },
  {
    name: 'dosing',
    label: 'Study dosing',
    subLabel: 'Includes study groups and treatments',
  },
];

const selectedTypesFromForm = (values: ExportFormValues): Array<ExportTypeName> => {
  const names: Array<ExportTypeName> = ['master', 'summary', 'prism', 'dosing'];
  return names.reduce<Array<ExportTypeName>>((acc, name) => {
    if (values[name]) {
      acc.push(name);
    }
    return acc;
  }, []);
};

const triggerFileDownload = (data: Blob, headers: Record<string, string>): void => {
  const fileName = headers['file-name'];
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Rendered under the "New Export" tab of the tabbed StudyExports page. Offers
 * legacy bundled exports (raw data / summary / Prism / dosing) plus the audit-log
 * report. Receives the resolved Study from the parent — no internal fetch.
 */
const ExportV2: FC<ExportV2Props> = ({ study }) => {
  const isMounted = useMountedState();

  const [loading, setLoading] = useState(false);
  const [auditLogGenerating, setAuditLogGenerating] = useState(false);
  const [apiError, setApiError] = useState<unknown>(null);
  const [auditLogError, setAuditLogError] = useState<unknown>(null);

  const { invoke: createAuditLogExport } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/exports/audit-logs',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'audit-log-async-export' } },
  });

  const { invoke: triggerExportGenerate } = useLegacyApiHook({
    method: 'POST',
    apiRoute: 'exports.generate',
    options: { invokeOnInit: false, onError: { toast: false, throw: false, slug: 'exports-generate' } },
  });

  const exportTypes = buildExportTypes(study);

  const { handleSubmit, control } = useForm<ExportFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const watchedValues = useWatch({ control }) as Partial<ExportFormValues>;
  const noTypesSelected =
    watchedValues.master !== true &&
    watchedValues.summary !== true &&
    watchedValues.prism !== true &&
    watchedValues.dosing !== true;

  // TODO: Remove when adding in async raw study data export: BNCH-147712
  const submitExport = async (values: ExportFormValues, emailFile: boolean): Promise<void> => {
    const payload = { types: selectedTypesFromForm(values) };
    setLoading(true);
    setApiError(null);

    try {
      if (emailFile) {
        await Http({
          method: 'post',
          url: apiRoute('studies.export.create.email', { id: study.id }),
          data: payload,
        });
      } else {
        const { data, headers } = await Http({
          method: 'post',
          url: apiRoute('studies.export.create.download', { id: study.id }),
          data: payload,
          responseType: 'blob',
        });
        triggerFileDownload(data as Blob, headers as Record<string, string>);
      }
      if (isMounted()) {
        successToast(`Successfully exported ${study.name}.`);
      }
    } catch (error) {
      if (isMounted() && notAborted(error)) {
        setApiError(error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const onDownloadClick = handleSubmit((values) => submitExport(values, false));
  const onEmailClick = handleSubmit((values) => submitExport(values, true));

  const generateAuditLogReport = async (): Promise<void> => {
    setAuditLogGenerating(true);
    setAuditLogError(null);

    const response = await createAuditLogExport({ path: { studyApiId: study.api_id } });

    if (response.type !== ResponseType.Success) {
      setAuditLogError(response.error);
      setAuditLogGenerating(false);
      return;
    }

    triggerExportGenerate({ path: { exportApiId: response.body.api_id } }).catch(() => {});
    infoToast('Export queued — see the Exports tab when ready.');
    setAuditLogGenerating(false);
  };

  const disableActions = loading || noTypesSelected;

  if (loading) {
    return (
      <div className="w-100 h-100">
        <Loading />
      </div>
    );
  }

  return (
    <div data-testid="study-exports__generate">
      <div className="ui-card mt3 ph3 pb3 mw6">
        {_notNil(apiError) && <ApiErrorBanner error={apiError} />}
        <form>
          <div>
            {exportTypes.map((type) => (
              <div className="mv3 pb3 bb" key={type.name}>
                <Controller
                  control={control}
                  name={type.name}
                  render={({ field: { onChange, value, name } }) => (
                    <Checkbox
                      label={type.label}
                      sublabel={type.subLabel}
                      id={type.name}
                      disabled={type.disabled === true}
                      name={name}
                      checked={value}
                      onChange={(event) => onChange(event.target.checked)}
                    />
                  )}
                />
              </div>
            ))}
          </div>
          <Button disabled={disableActions} onClick={onDownloadClick}>
            Download
          </Button>
          <Button disabled={disableActions} onClick={onEmailClick} className="ml3 plain">
            Send to your email
          </Button>
        </form>
      </div>

      <div
        className="ui-card pa3 mt4 mw6"
        data-test-component="ExportV2"
        data-test-element="audit-log-section"
        data-testid="export-audit-log-section"
      >
        <h3 className="f5 normal pb2">Generate audit log</h3>
        <p className="pb3 bb f6 ma0 dark-gray">
          A historical log of samples, measurements, observations etc. The export will appear in the Exports tab when
          ready.
        </p>
        {_notNil(auditLogError) && <ApiErrorBanner error={auditLogError} />}
        <form className="pt3">
          <Button
            className="plain"
            disabled={auditLogGenerating || loading}
            onClick={generateAuditLogReport}
            testKey="audit-log-generate"
          >
            Generate audit log
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ExportV2;
